import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Heart, Search, GripVertical, X, Plus, ArrowLeft, Save, RefreshCw,
  AlertCircle, CheckCircle2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { extractErrorMessage } from '../../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFeatured = () => {
  const { hasPermission } = useAuth();
  const canAccess = hasPermission('featured');
  const navigate = useNavigate();

  const [state, setState] = useState(null); // { items, updated_at, days_old, is_stale, min_count, max_count }
  const [selection, setSelection] = useState([]); // ordered list of product objects
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Search state
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Drag state
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  useEffect(() => {
    if (!canAccess) navigate('/connexion');
  }, [canAccess, navigate]);

  const fetchState = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/featured-products`);
      setState(res.data);
      setSelection(res.data.items || []);
      setDirty(false);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (!q || q.length < 2) { setResults([]); return undefined; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/products?search=${encodeURIComponent(q)}`
        );
        setResults(res.data || []);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.debug('[featured]', e);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const min = state?.min_count || 6;
  const max = state?.max_count || 12;
  const count = selection.length;
  const isValid = count >= min && count <= max;
  const selectedIds = useMemo(() => new Set(selection.map((p) => p.id)), [selection]);

  const addProduct = (product) => {
    if (selectedIds.has(product.id)) return;
    if (count >= max) {
      toast.warning(`Maximum ${max} produits autorisés`);
      return;
    }
    setSelection((prev) => [...prev, product]);
    setDirty(true);
    setQuery('');
    setResults([]);
  };

  const removeProduct = (id) => {
    setSelection((prev) => prev.filter((p) => p.id !== id));
    setDirty(true);
  };

  const moveItem = (from, to) => {
    if (from === to || to < 0 || to >= selection.length) return;
    setSelection((prev) => {
      const copy = [...prev];
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    });
    setDirty(true);
  };

  // --- HTML5 native drag-and-drop ---
  const onDragStart = (idx) => (e) => {
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox
    e.dataTransfer.setData('text/plain', String(idx));
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    if (draggedIdx !== null && draggedIdx !== idx) moveItem(draggedIdx, idx);
    setDraggedIdx(null);
    setDragOverIdx(null);
  };
  const onDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const save = async () => {
    if (!isValid) {
      toast.error(`Sélection invalide : entre ${min} et ${max} produits requis`);
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/featured-products`, {
        product_ids: selection.map((p) => p.id),
      });
      toast.success('Sélection enregistrée');
      fetchState();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Échec de l'enregistrement"));
    } finally {
      setSaving(false);
    }
  };

  const keepCurrent = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/featured-products/keep`);
      toast.success('Sélection conservée pour la semaine');
      fetchState();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16" data-testid="admin-featured-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/admin"
            className="text-zinc-500 hover:text-[color:var(--color-primary)] text-xs flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Tableau de bord
          </Link>
          <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">
            Coups de cœur de la semaine
          </p>
          <h1 className="text-3xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Sélection mise en avant sur l'accueil
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Choisissez {min} à {max} produits — glissez pour réorganiser, supprimez pour libérer une place.
          </p>
        </div>

        {/* Status banner */}
        {state && (
          <StatusBanner
            state={state}
            count={count}
            onKeep={keepCurrent}
            saving={saving}
          />
        )}

        {loading ? (
          <p className="text-zinc-500 text-sm py-12 text-center">Chargement…</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Selection */}
            <div className="lg:col-span-2 border border-white/10 bg-[color:var(--color-surface)] p-5"
                 data-testid="featured-selection">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em]">
                  Sélection actuelle
                </p>
                <p className={`text-xs ${isValid ? 'text-emerald-400' : 'text-orange-400'}`}>
                  <span className="font-medium" data-testid="featured-count">{count}</span>
                  {' '}/ {min}–{max}
                </p>
              </div>

              {selection.length === 0 ? (
                <p className="text-zinc-600 text-sm italic py-8 text-center">
                  Aucun produit sélectionné. Recherchez et ajoutez-en au moins {min} ci-contre.
                </p>
              ) : (
                <ul className="space-y-2" data-testid="featured-list">
                  {selection.map((p, idx) => (
                    <li key={p.id}
                        draggable
                        onDragStart={onDragStart(idx)}
                        onDragOver={onDragOver(idx)}
                        onDrop={onDrop(idx)}
                        onDragEnd={onDragEnd}
                        data-testid={`featured-item-${p.id}`}
                        className={`flex items-center gap-3 border bg-black p-2.5 cursor-grab active:cursor-grabbing transition-colors
                          ${dragOverIdx === idx ? 'border-[color:var(--color-primary)]' : 'border-white/5'}
                          ${draggedIdx === idx ? 'opacity-50' : ''}`}>
                      <GripVertical className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                      <span className="text-[color:var(--color-primary)] text-xs w-5 text-right">{idx + 1}</span>
                      {p.image_url && (
                        <img src={p.image_url} alt=""
                          className="w-12 h-12 object-cover bg-white/5 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm truncate">{p.name}</p>
                        <p className="text-zinc-500 text-xs truncate">
                          {p.brand} · stock {p.stock} · {Number(p.price_gros || 0).toFixed(2)}€ gros
                        </p>
                      </div>
                      <button type="button"
                        onClick={() => removeProduct(p.id)}
                        data-testid={`featured-remove-${p.id}`}
                        className="text-zinc-600 hover:text-red-400 p-1">
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-white/10">
                <button type="button"
                  onClick={save}
                  disabled={saving || !dirty || !isValid}
                  data-testid="featured-save"
                  className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <Save className="w-3.5 h-3.5" />
                  Enregistrer la sélection
                </button>
                {dirty && (
                  <button type="button"
                    onClick={fetchState}
                    disabled={saving}
                    data-testid="featured-discard"
                    className="flex items-center gap-2 border border-white/10 text-zinc-400 px-4 py-2 text-xs uppercase tracking-wider hover:border-white/30 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" />
                    Annuler les changements
                  </button>
                )}
                <p className="text-zinc-600 text-xs ml-auto">
                  {dirty
                    ? 'Modifications non enregistrées'
                    : 'Sélection à jour'}
                </p>
              </div>
            </div>

            {/* Search */}
            <div className="lg:col-span-1 border border-white/10 bg-[color:var(--color-surface)] p-5"
                 data-testid="featured-search">
              <p className="text-[color:var(--color-primary)] text-[10px] uppercase tracking-[0.25em] mb-3">
                Ajouter un produit
              </p>
              <div className="relative mb-3">
                <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nom, marque, code-barres…"
                  data-testid="featured-search-input"
                  className="w-full bg-black border border-white/10 text-white pl-10 pr-3 py-2.5 text-sm focus:border-[color:var(--color-primary)] outline-none"
                />
              </div>
              {searching && <p className="text-zinc-500 text-xs mb-2">Recherche…</p>}
              {results.length > 0 && (
                <ul className="max-h-[480px] overflow-y-auto divide-y divide-white/5 border border-white/5">
                  {results.map((p) => {
                    const already = selectedIds.has(p.id);
                    return (
                      <li key={p.id}>
                        <button type="button"
                          onClick={() => addProduct(p)}
                          disabled={already || count >= max}
                          data-testid={`featured-add-${p.id}`}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-left">
                          {p.image_url && (
                            <img src={p.image_url} alt=""
                              className="w-8 h-8 object-cover bg-white/5 flex-shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-xs truncate">{p.name}</p>
                            <p className="text-zinc-500 text-[10px] truncate">{p.brand}</p>
                          </div>
                          {already ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <Plus className="w-4 h-4 text-[color:var(--color-primary)]" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {query.trim() && !searching && results.length === 0 && (
                <p className="text-zinc-600 text-xs italic">Aucun produit trouvé</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =========================================================================

const StatusBanner = ({ state, count, onKeep, saving }) => {
  const hasSelection = (state.product_ids?.length || 0) > 0;
  const isStale = state.is_stale;
  const days = state.days_old;

  if (!hasSelection) {
    return (
      <div className="border border-orange-500/40 bg-orange-500/10 p-4 mb-6 flex items-start gap-3"
           data-testid="featured-banner-empty">
        <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-orange-300 text-sm font-medium">
            Aucune sélection active sur l'accueil
          </p>
          <p className="text-zinc-400 text-xs mt-1">
            Ajoutez {state.min_count} à {state.max_count} produits pour activer la section
            « ✨ Coups de cœur de la semaine » sur la page d'accueil.
          </p>
        </div>
      </div>
    );
  }

  if (isStale) {
    return (
      <div className="border border-[color:rgb(var(--primary-rgb)/0.50)] bg-[color:rgb(var(--primary-rgb)/0.10)] p-4 mb-6 flex items-start gap-3"
           data-testid="featured-banner-stale">
        <Heart className="w-5 h-5 text-[color:var(--color-primary)] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-[color:var(--color-primary)] text-sm font-medium">
            Vos coups de cœur datent de {days} jour{days > 1 ? 's' : ''}
          </p>
          <p className="text-zinc-400 text-xs mt-1">
            Profitez-en pour renouveler la sélection, ou conservez-la telle quelle pour la semaine prochaine.
          </p>
        </div>
        <button type="button"
          onClick={onKeep}
          disabled={saving}
          data-testid="featured-keep"
          className="flex-shrink-0 border border-[color:var(--color-primary)] text-[color:var(--color-primary)] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary)] hover:text-black transition-colors disabled:opacity-50">
          Garder cette semaine
        </button>
      </div>
    );
  }

  return (
    <div className="border border-emerald-500/30 bg-emerald-500/5 p-3 mb-6 flex items-center gap-3 text-sm"
         data-testid="featured-banner-fresh">
      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
      <p className="text-zinc-300">
        Sélection à jour ({count} produits, modifiée il y a {days || 0} jour{(days || 0) > 1 ? 's' : ''}).
      </p>
    </div>
  );
};

export default AdminFeatured;
